export const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: number
  return (...args: Parameters<F>): Promise<ReturnType<F>> =>
    new Promise((resolve) => {
      if (timeout) {
        clearTimeout(timeout)
      }
      timeout = setTimeout(() => resolve(func(...args)), waitFor)
    })
}

export const throttle = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  const now = () => new Date().getTime()
  const resetStartTime = () => startTime = now()
  let timeout: number
  let startTime: number = now() - waitFor
  return (...args: Parameters<F>): Promise<ReturnType<F>> =>
    new Promise((resolve) => {
      const timeLeft = (startTime + waitFor) - now()
      if (timeout) {
        clearTimeout(timeout)
      }
      if (startTime + waitFor <= now()) {
        resetStartTime()
        resolve(func(...args))
      } else {
        timeout = setTimeout(() => {
          resetStartTime()
          resolve(func(...args))
        }, timeLeft)
      }
    })
}

/*
 // Example usage
const func = (hello: string) => {
  console.log(hello)
}
const dfunc = debounce<typeof func>(func, 1000)
dfunc('hello 1')
dfunc('hello 2')
dfunc('hello 3')
> hello 3

const func = (hello: string) => { console.log(new Date().getTime(), '>>>', hello) }
const throttledFunc = throttle<typeof func>(func, 1000)
throttledFunc('hello 1')
setTimeout(() => throttledFunc('hello 2'), 450)
setTimeout(() => throttledFunc('hello 3'), 950)
setTimeout(() => throttledFunc('hello 4'), 1700)
setTimeout(() => throttledFunc('hello 4.1'), 1700)
setTimeout(() => throttledFunc('hello 4.2'), 1750)
setTimeout(() => throttledFunc('hello 4.3'), 1995)
setTimeout(() => throttledFunc('hello 4.4'), 2000)
setTimeout(() => throttledFunc('hello 4.5'), 2010)
setTimeout(() => throttledFunc('hello 5'), 2100)
*/

export function deepMerge(target: any, source: any) {
  if (typeof source !== "object") return target
  Object.entries(source).forEach(([key, value]) => {
    if (value instanceof Map || value instanceof Array) {
      target[key] = value
    } else if (value && typeof value === "object") {
      return deepMerge(target[key] = target[key] || {}, value)
    }
    target[key] = value
  })
  return target
}

export function mergeWidgetConfig(target: any, source: any, level = 0) {
  Object.entries(source).forEach(([key, value]) => {
    // check if target has extra keys and strip them off
    (level < 2) && Object.entries(target).forEach(([tKey, tVal]) => {
      if (!(tKey in source)) {
        if (["wc", "i", "widget", "moved", "componentPath", "_description"].indexOf(tKey) === -1) {
          console.debug("Deleted key from config:", tKey)
          delete target[tKey]
        }
      }
    })
    if (value instanceof Map || value instanceof Array) {
      target[key] = value
    } else if (value && typeof value === "object") {
      mergeWidgetConfig(target[key] = target[key] || {}, value, level + 1)
      return
    }
    if (typeof target === "object" && !(key in target)) {
      target[key] = value
    }
  })
  return target
}

export function deepCopy(src1: any, src2: any) {
  const target = Object.assign({}, src1)
  deepMerge(target, src2)
  return target
}

// scans object for keys starting with `prefix` and removes all matching keys
// e.t. {a: 'bbb', _c: 'ddd'} will become {a: 'bbb'}
export function filterPrefix(source: any, prefix = "_") {
  let target: any = {}
  Object.entries(source).forEach(([key, value]) => {
    if (!key.startsWith(prefix)) {
      if (value && typeof value === 'object') {
        target[key] = filterPrefix(value, prefix)
      } else {
        target[key] = value
      }
    }
  })
  return target
}
